import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CheckOutline, DuplicateOutline, Flex, IconButton, Tooltip, useToast } from '@bonusly/design-system';

import type { BizyUiBlockAction } from '../../../schemas/bizy-ui-blocks';
import { useBizyUiBlockActionDispatcher } from '../lib/ui-block-action-dispatcher';

type ActionVariant = { variant: 'solid' | 'ghost'; colorScheme: 'primary' | 'secondary' };

/**
 * Per-action visual treatment. The committing action is a solid PRIMARY
 * button; lighter alternatives are solid SECONDARY buttons; pure
 * navigation / escape hatches are ghost.
 */
export function variantFor(action: BizyUiBlockAction): ActionVariant {
	switch (action.type) {
		case 'send_now':
			// A comment/add-on commit (carries a parent post — "Comment now" /
			// "Add now") is the solid primary button, matching every other
			// card's primary CTA. The recognition give-box "Give now" keeps its
			// existing ghost treatment (open_in_givebox is its primary), so that
			// card is unchanged.
			return action.parentBonusId ? { variant: 'solid', colorScheme: 'primary' } : { variant: 'ghost', colorScheme: 'primary' };
		case 'insert_into_draft':
			// "Edit first" — the lighter secondary, using the same ghost style
			// the other cards use for their non-primary action.
			return { variant: 'ghost', colorScheme: 'primary' };
		case 'open_in_givebox':
		case 'commit_user_action':
		case 'request_draft':
			return { variant: 'solid', colorScheme: 'primary' };
		case 'view_post':
		case 'dismiss':
		case 'cancel_user_action':
			return { variant: 'ghost', colorScheme: 'secondary' };
	}
}

// Ghost actions sort first (left); the solid commit button lands on the right
// edge. (variantFor only ever returns ghost or solid-primary, so this is a
// simple two-tier ordering.)
function actionWeight(action: BizyUiBlockAction): number {
	return variantFor(action).variant === 'ghost' ? 0 : 1;
}

/**
 * Prefer `action.label` when the backend specified one; otherwise
 * fall back to a translated default per action type.
 */
function useActionLabel(action: BizyUiBlockAction): string {
	const { t } = useTranslation('admin_bizy');
	if (action.label) return action.label;
	switch (action.type) {
		case 'insert_into_draft':
			return t('uiBlocks.actions.insertIntoDraft');
		case 'send_now':
			return t('uiBlocks.actions.sendNow');
		case 'open_in_givebox':
			return t('uiBlocks.actions.openInGivebox');
		case 'view_post':
			return t('uiBlocks.actions.viewPost');
		case 'dismiss':
			return t('uiBlocks.actions.dismiss');
		case 'commit_user_action':
			return t('uiBlocks.actions.confirm');
		case 'cancel_user_action':
			return t('uiBlocks.actions.cancel');
		case 'request_draft':
			return t('uiBlocks.actions.requestDraft');
	}
}

/**
 * Action bar rendered at the bottom of a UI block card. Renders a
 * left-side copy-to-clipboard icon (when `copyText` is provided) and a
 * right-aligned row of action buttons.
 *
 * Copy is a pure client-side affordance — the text comes from the
 * card's payload, not an action declaration — so it doesn't round-trip
 * through the dispatcher. It lets the user grab the draft even when
 * they don't want Send now / Edit in givebox.
 *
 * Actions are sorted so the primary (solid) commit action lands on
 * the right, matching recognition / comment card mocks.
 */
export function UiBlockActionBar({
	actions,
	copyText,
	areActionsDisabled = false,
	oneShot = false,
}: {
	actions: readonly BizyUiBlockAction[];
	copyText?: string;
	areActionsDisabled?: boolean;
	/**
	 * When true, the first action click disables every button in the bar.
	 * Used by mutating cards (user-admin Confirm/Cancel) so an admin can't
	 * fire the same commit twice by double-clicking — each card resolves to
	 * exactly one action. Draft/handoff cards (recognition, comment) leave
	 * this false so re-opening the give-box etc. stays available.
	 */
	oneShot?: boolean;
}) {
	const dispatch = useBizyUiBlockActionDispatcher();
	const [hasActed, setHasActed] = useState(false);
	// Synchronous guard: state updates are async, so two clicks fired in the
	// same frame would both pass the `hasActed` check and dispatch before the
	// re-render disables the buttons. The ref flips immediately on the first
	// dispatch, so the second click is dropped before it can post a duplicate
	// admin mutation.
	const actedRef = useRef(false);

	if (actions.length === 0 && !copyText) return null;

	// Sort so secondary/ghost actions come first, the primary commit lands on the right.
	const ordered = [...actions].sort((a, b) => actionWeight(a) - actionWeight(b));

	const buttonsDisabled = areActionsDisabled || (oneShot && hasActed);

	const handleAction = (action: BizyUiBlockAction) => {
		if (oneShot) {
			if (actedRef.current) return;
			actedRef.current = true;
			setHasActed(true);
		}
		dispatch(action);
	};

	return (
		<Flex justify="space-between" align="center" gap={2} rowGap={2} mt={3} flexWrap="wrap">
			{copyText && (
				<Flex align="center" flexShrink={0}>
					<CopyButton text={copyText} />
				</Flex>
			)}
			{/* Grow to fill the row and right-align; wrap the buttons onto a new
			    line (with minW=0 so they can shrink) instead of overflowing the
			    card in the narrow side panel. */}
			<Flex align="center" gap={2} rowGap={2} flexWrap="wrap" justify="flex-end" flex="1 1 auto" minW={0}>
				{ordered.map((action, index) => (
					<ActionButton key={`${action.type}-${index}`} action={action} isDisabled={buttonsDisabled} onAction={handleAction} />
				))}
			</Flex>
		</Flex>
	);
}

/**
 * Small icon button that writes `text` to the clipboard and briefly
 * flips to a checkmark. No analytics (the user hasn't committed to any
 * action), no dispatcher (pure DOM side-effect). A failed clipboard
 * write surfaces a toast so the user can tell it didn't work.
 */
function CopyButton({ text }: { text: string }) {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copied, setCopied] = useState(false);
	const handleCopy = () => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 2000 });
			});
	};
	return (
		<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
			<IconButton
				aria-label={t('feedback.copy')}
				icon={copied ? <CheckOutline boxSize={4} /> : <DuplicateOutline boxSize={4} />}
				size="sm"
				variant="ghost"
				colorScheme="secondary"
				color="text.softer"
				onClick={handleCopy}
			/>
		</Tooltip>
	);
}

function ActionButton({ action, isDisabled, onAction }: { action: BizyUiBlockAction; isDisabled: boolean; onAction: (action: BizyUiBlockAction) => void }) {
	const label = useActionLabel(action);
	const { variant, colorScheme } = variantFor(action);
	return (
		<Button variant={variant} colorScheme={colorScheme} size="sm" flexShrink={0} onClick={() => onAction(action)} isDisabled={isDisabled}>
			{label}
		</Button>
	);
}
